import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  IconButton,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Remove as RemoveIcon,
  Edit as EditRoleIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import DataTable from '../components/DataTable';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo?: string;
  website?: string;
  industry?: string;
  size?: string;
  isActive: boolean;
  defaultTasks?: string[];
  createdAt: string;
  updatedAt: string;
  subscription?: {
    status: string;
    seatsPurchased: number;
    seatsUsed: number;
  };
}

interface OrganizationFormData {
  name: string;
  slug: string;
  description: string;
  website: string;
  industry: string;
  size: string;
  isActive: boolean;
  defaultTasks: string[];
}

interface User {
  id: string;
  email: string;
  displayName?: string;
  isVerified: boolean;
  isActive: boolean;
  systemRole?: string;
}

interface UserOrganization {
  userId: string;
  organizationId: string;
  role: string;
  joinedAt: string;
  isActive: boolean;
  user: {
    id: string;
    email: string;
    displayName?: string;
  };
}

export default function Organizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [organizationMemberCounts, setOrganizationMemberCounts] = useState<{[key: string]: number}>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const { formatDateOnly } = useDateFormat();
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<UserOrganization[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState('MEMBER');
  const [editingMember, setEditingMember] = useState<UserOrganization | null>(null);
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    slug: '',
    description: '',
    website: '',
    industry: '',
    size: '',
    isActive: true,
    defaultTasks: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadOrganizations();
    loadUsers();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getOrganizations();
      const orgs = response.data || [];
      
      // Load member counts and subscription data for each organization
      const memberCounts: {[key: string]: number} = {};
      const orgsWithSubscriptionData = await Promise.all(
        orgs.map(async (org: Organization) => {
          try {
            // Load member count
            const membersResponse = await api.getOrganizationUsers(org.id);
            memberCounts[org.id] = membersResponse.data?.length || 0;
            
            // Load subscription data
            try {
              const subscriptionResponse = await api.getSubscription(org.id);
              if (subscriptionResponse && subscriptionResponse.subscription) {
                return {
                  ...org,
                  subscription: {
                    status: subscriptionResponse.subscription.status,
                    seatsPurchased: subscriptionResponse.subscription.seatsPurchased,
                    seatsUsed: subscriptionResponse.subscription.seatsUsed
                  }
                };
              }
            } catch (subscriptionErr) {
              // No subscription found or error - this is normal for orgs without subscriptions
              console.log(`No subscription found for org ${org.id}:`, subscriptionErr);
            }
            
            return org;
          } catch (err) {
            console.error(`Error loading data for org ${org.id}:`, err);
            memberCounts[org.id] = 0;
            return org;
          }
        })
      );
      
      setOrganizations(orgsWithSubscriptionData);
      setOrganizationMemberCounts(memberCounts);
    } catch (err: any) {
      console.error('Error loading organizations:', err);
      setError(err.response?.data?.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.getUsers();
      setUsers(response.data || []);
    } catch (err: any) {
      console.error('Error loading users:', err);
    }
  };

  const loadMembers = async (orgId: string) => {
    try {
      const response = await api.getOrganizationUsers(orgId);
      // Transform the data to match the expected UserOrganization format
      const transformedMembers = response.data?.map((user: any) => ({
        userId: user.id,
        organizationId: orgId,
        role: user.organizationRole,
        joinedAt: user.joinedAt,
        isActive: true,
        user: {
          id: user.id,
          email: user.email,
          displayName: user.displayName,
          isActive: user.isActive || true,
          isVerified: user.isVerified || false
        }
      })) || [];
      setMembers(transformedMembers);
    } catch (err: any) {
      console.error('Error loading members:', err);
      setError(err.response?.data?.message || 'Failed to load members');
    }
  };

  const handleOpenDialog = (org?: Organization) => {
    if (org) {
      setEditingOrg(org);
      setFormData({
        name: org.name,
        slug: org.slug,
        description: org.description || '',
        website: org.website || '',
        industry: org.industry || '',
        size: org.size || '',
        isActive: org.isActive,
        defaultTasks: org.defaultTasks || []
      });
    } else {
      setEditingOrg(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        website: '',
        industry: '',
        size: '',
        isActive: true,
        defaultTasks: []
      });
    }
    setDialogOpen(true);
  };

  const handleOpenMembersDialog = async (org: Organization) => {
    setSelectedOrg(org);
    await loadMembers(org.id);
    setMembersDialogOpen(true);
  };

  const handleOpenAddUserDialog = async (org: Organization) => {
    setSelectedOrg(org);
    await loadMembers(org.id);
    
    // Filter out users who are already members
    const memberUserIds = members.map(m => m.userId);
    const available = users.filter(user => !memberUserIds.includes(user.id));
    setAvailableUsers(available);
    
    setAddUserDialogOpen(true);
  };

  const handleOpenEditRoleDialog = (member: UserOrganization) => {
    setEditingMember(member);
    setSelectedRole(member.role);
    setEditRoleDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingOrg(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      website: '',
      industry: '',
      size: '',
      isActive: true,
      defaultTasks: []
    });
  };

  const handleCloseMembersDialog = () => {
    setMembersDialogOpen(false);
    setSelectedOrg(null);
    setMembers([]);
  };

  const handleCloseAddUserDialog = () => {
    setAddUserDialogOpen(false);
    setSelectedOrg(null);
    setSelectedUser(null);
    setSelectedRole('MEMBER');
    setAvailableUsers([]);
  };

  const handleCloseEditRoleDialog = () => {
    setEditRoleDialogOpen(false);
    setEditingMember(null);
    setSelectedRole('MEMBER');
  };

  const handleSubmit = async () => {
    try {
      if (editingOrg) {
        await api.updateOrganization(editingOrg.id, formData);
        // If we're editing and the status changed to inactive, reset filter to show all
        if (editingOrg.isActive !== formData.isActive && !formData.isActive) {
          setFilterStatus('all');
        }
      } else {
        await api.createOrganization(formData);
      }
      handleCloseDialog();
      loadOrganizations();
    } catch (err: any) {
      console.error('Error saving organization:', err);
      setError(err.response?.data?.message || 'Failed to save organization');
    }
  };

  const handleAddUserToOrganization = async () => {
    if (!selectedUser || !selectedOrg) return;

    try {
      await api.createUserOrganization({
        userId: selectedUser.id,
        organizationId: selectedOrg.id,
        role: selectedRole
      });
      
      handleCloseAddUserDialog();
      await loadMembers(selectedOrg.id);
      setError(null);
    } catch (err: any) {
      console.error('Error adding user to organization:', err);
      setError(err.response?.data?.message || 'Failed to add user to organization');
    }
  };

  const handleUpdateUserRole = async () => {
    if (!editingMember || !selectedOrg) return;

    try {
      await api.updateUserOrganization(editingMember.userId, selectedOrg.id, {
        role: selectedRole
      });
      
      handleCloseEditRoleDialog();
      await loadMembers(selectedOrg.id);
      setError(null);
    } catch (err: any) {
      console.error('Error updating user role:', err);
      setError(err.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleRemoveUserFromOrganization = async (member: UserOrganization) => {
    if (!selectedOrg) return;

    if (window.confirm(`Are you sure you want to remove ${member.user.displayName || member.user.email} from this organization?`)) {
      try {
        await api.deleteUserOrganization(member.userId, selectedOrg.id);
        await loadMembers(selectedOrg.id);
        setError(null);
      } catch (err: any) {
        console.error('Error removing user from organization:', err);
        setError(err.response?.data?.message || 'Failed to remove user from organization');
      }
    }
  };

  const handleDeleteOrganization = async (orgId: string) => {
    if (window.confirm('Are you sure you want to delete this organization?')) {
      try {
        await api.deleteOrganization(orgId);
        loadOrganizations();
      } catch (err: any) {
        console.error('Error deleting organization:', err);
        setError(err.response?.data?.message || 'Failed to delete organization');
      }
    }
  };

  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (org.description && org.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && org.isActive) ||
                         (filterStatus === 'inactive' && !org.isActive);
    return matchesSearch && matchesStatus;
  });

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'slug', headerName: 'Slug', width: 150 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { field: 'industry', headerName: 'Industry', width: 120 },
    { field: 'size', headerName: 'Size', width: 100 },
    { 
      field: 'memberCount', 
      headerName: 'Members', 
      width: 100,
      renderCell: (params) => organizationMemberCounts[params.row.id] || 0
    },
    { 
      field: 'subscriptionStatus', 
      headerName: 'Subscription', 
      width: 120,
      renderCell: (params) => {
        const subscription = params.row.subscription;
        if (!subscription) {
          return <Chip label="Inactive" color="error" size="small" />;
        }
        
        const getStatusColor = (status: string) => {
          switch (status) {
            case 'active': return 'success';
            case 'trialing': return 'warning';
            case 'canceled': return 'error';
            default: return 'default';
          }
        };
        
        const getStatusLabel = (status: string) => {
          switch (status) {
            case 'active': return 'Active';
            case 'trialing': return 'Trial';
            case 'canceled': return 'Canceled';
            default: return status;
          }
        };
        
        return (
          <Chip 
            label={getStatusLabel(subscription.status)} 
            color={getStatusColor(subscription.status) as any}
            size="small"
          />
        );
      }
    },
    { 
      field: 'seats', 
      headerName: 'Seats', 
      width: 100,
      renderCell: (params) => {
        const subscription = params.row.subscription;
        if (!subscription) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        
        const { seatsPurchased, seatsUsed } = subscription;
        const available = seatsPurchased - seatsUsed;
        
        return (
          <Typography variant="body2">
            <strong>{available}</strong>/{seatsPurchased}
          </Typography>
        );
      }
    },
    { 
      field: 'isActive', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Active' : 'Inactive'} 
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    { 
      field: 'createdAt', 
      headerName: 'Created', 
      width: 120,
      valueFormatter: (params) => formatDateOnly(params.value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 200,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<PeopleIcon />}
          label="Members"
          onClick={() => handleOpenMembersDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteOrganization(params.row.id)}
        />
      ]
    }
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Organizations Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Organization
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search organizations"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                {filteredOrganizations.length} of {organizations.length} organizations
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Organizations Table */}
      <DataTable
        rows={filteredOrganizations}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 50 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Add/Edit Organization Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingOrg ? 'Edit Organization' : 'Add New Organization'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const slug = name.toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-+|-+$/g, '');
                    setFormData({ ...formData, name, slug });
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  helperText="URL-friendly identifier (auto-generated from name)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    value={formData.industry}
                    label="Industry"
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  >
                    <MenuItem value="">Select Industry</MenuItem>
                    <MenuItem value="Technology">Technology</MenuItem>
                    <MenuItem value="Healthcare">Healthcare</MenuItem>
                    <MenuItem value="Finance">Finance</MenuItem>
                    <MenuItem value="Education">Education</MenuItem>
                    <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                    <MenuItem value="Retail">Retail</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Size</InputLabel>
                  <Select
                    value={formData.size}
                    label="Size"
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  >
                    <MenuItem value="">Select Size</MenuItem>
                    <MenuItem value="1-10">1-10 employees</MenuItem>
                    <MenuItem value="11-50">11-50 employees</MenuItem>
                    <MenuItem value="51-200">51-200 employees</MenuItem>
                    <MenuItem value="201-1000">201-1000 employees</MenuItem>
                    <MenuItem value="1000+">1000+ employees</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Default Tasks
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These tasks will be automatically available for all new companies created in this organization.
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.defaultTasks}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, defaultTasks: newValue });
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Default Tasks"
                      placeholder="Type a task name and press Enter"
                      helperText="Press Enter to add a new task"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingOrg ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={membersDialogOpen} onClose={handleCloseMembersDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Members - {selectedOrg?.name} ({members.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => handleOpenAddUserDialog(selectedOrg!)}
            >
              Add User
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ height: '70vh', p: 0 }}>
          <DataGrid
            rows={members.map(member => ({
              id: member.userId,
              name: member.user.displayName || member.user.email,
              email: member.user.email,
              role: member.role,
              joinedAt: member.joinedAt,
              isActive: member.isActive
            }))}
            columns={[
              {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 200,
                renderCell: (params) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                      {params.value?.charAt(0) || params.row.email.charAt(0)}
                    </Avatar>
                    <Typography variant="body2" noWrap>
                      {params.value}
                    </Typography>
                  </Box>
                )
              },
              {
                field: 'email',
                headerName: 'Email',
                flex: 1,
                minWidth: 250,
                renderCell: (params) => (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {params.value}
                  </Typography>
                )
              },
              {
                field: 'role',
                headerName: 'Role',
                width: 120,
                renderCell: (params) => (
                  <Chip
                    label={params.value}
                    color={
                      params.value?.toLowerCase() === 'owner' ? 'warning' :
                      params.value?.toLowerCase() === 'manager' ? 'primary' : 'default'
                    }
                    size="small"
                  />
                )
              },
              {
                field: 'joinedAt',
                headerName: 'Joined',
                width: 120,
                renderCell: (params) => (
                  <Typography variant="body2" color="text.secondary">
                    {formatDateOnly(params.value)}
                  </Typography>
                )
              },
              {
                field: 'isActive',
                headerName: 'Status',
                width: 100,
                renderCell: (params) => (
                  <Chip
                    label={params.value ? 'Active' : 'Inactive'}
                    color={params.value ? 'success' : 'error'}
                    size="small"
                  />
                )
              },
              {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                width: 120,
                getActions: (params) => [
                  <GridActionsCellItem
                    icon={<EditRoleIcon />}
                    label="Edit Role"
                    onClick={() => {
                      const member = members.find(m => m.userId === params.id);
                      if (member) handleOpenEditRoleDialog(member);
                    }}
                  />,
                  <GridActionsCellItem
                    icon={<RemoveIcon />}
                    label="Remove User"
                    onClick={() => {
                      const member = members.find(m => m.userId === params.id);
                      if (member) handleRemoveUserFromOrganization(member);
                    }}
                  />
                ]
              }
            ]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 }
              }
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMembersDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add User to Organization Dialog */}
      <Dialog open={addUserDialogOpen} onClose={handleCloseAddUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add User to {selectedOrg?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={availableUsers}
                  getOptionLabel={(option) => `${option.displayName || option.email} (${option.email})`}
                  value={selectedUser}
                  onChange={(_, newValue) => setSelectedUser(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      required
                      helperText="Search for a user to add to this organization"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={selectedRole}
                    label="Role"
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    <MenuItem value="MEMBER">Member</MenuItem>
                    <MenuItem value="MANAGER">Manager</MenuItem>
                    <MenuItem value="OWNER">Owner</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddUserDialog}>Cancel</Button>
          <Button 
            onClick={handleAddUserToOrganization} 
            variant="contained"
            disabled={!selectedUser}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Role Dialog */}
      <Dialog open={editRoleDialogOpen} onClose={handleCloseEditRoleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Role for {editingMember?.user.displayName || editingMember?.user.email}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={selectedRole}
                    label="Role"
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    <MenuItem value="MEMBER">Member</MenuItem>
                    <MenuItem value="MANAGER">Manager</MenuItem>
                    <MenuItem value="OWNER">Owner</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditRoleDialog}>Cancel</Button>
          <Button 
            onClick={handleUpdateUserRole} 
            variant="contained"
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 